import React from "react";
import { Check, Lock } from "lucide-react";
import { useT } from "@/lib/LanguageContext";
// Relative on purpose: pure modules, resolvable under Jest without a shim.
import { certificateUrl } from "../../pathways/learnerApi";
import { isFlatPath, isOpenable, isPathwayComplete, stepVisual } from "../../pathways/learnerModel";
import { PATHWAYS_LEARNER as IDS } from "../../constants/testIds/pathwaysLearner";
import { stepKindIcon, stepKindLabel } from "./stepKinds";

const ROW_BASE = "flex w-full items-center gap-3 rounded-2xl px-4 py-3 text-left text-sm";
const ROW_CLASSES = Object.freeze({
  done: "bg-eh-sage-soft text-eh-ink",
  current: "bg-eh-ochre/10 text-eh-ink font-medium ring-2 ring-eh-ochre-deep",
  open: "bg-eh-surface text-eh-ink border border-eh-border-soft",
  locked: "bg-eh-cream-deep text-eh-muted",
});

/** The round marker at the start of a row: a tick, a filled dot, a ring, or a padlock. */
function RowMarker({ visual }) {
  if (visual === "done") {
    return (
      <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-eh-sage-deep text-eh-cream" aria-hidden="true">
        <Check className="h-3.5 w-3.5" />
      </span>
    );
  }
  if (visual === "locked") return <Lock className="h-4 w-4 flex-shrink-0 text-eh-muted" aria-hidden="true" />;
  if (visual === "current") return <span className="h-6 w-6 flex-shrink-0 rounded-full bg-eh-ochre-deep" aria-hidden="true" />;
  return <span className="h-6 w-6 flex-shrink-0 rounded-full border-2 border-eh-border" aria-hidden="true" />;
}

/** The row's state for a screen reader, which cannot see the colour or the marker. */
function StateLabel({ visual }) {
  const t = useT("public");
  if (visual === "done") return <span className="sr-only">{t("pathways.state.done")}</span>;
  if (visual === "current") return <span className="sr-only">{t("pathways.state.current")}</span>;
  if (visual === "locked") return <span className="sr-only">{t("pathways.state.locked")}</span>;
  return null;
}

/**
 * One step row (spec screen 4, "Step states"): done (tick, green), current (highlighted),
 * optional (dashed, "Optional" pill), locked (padlock, "Unlocks after <lock.title>"), with
 * the kind's icon before the title (contract §10.0.1, six kinds) and "~N min" on the right. An openable row is a button to the step screen; a locked row is
 * not a control at all.
 */
function StepRow({ row, onOpenStep }) {
  const t = useT("public");
  const visual = stepVisual(row);
  const optional = !row.required;
  const dashed = optional && visual !== "done" && visual !== "current" ? "border-2 border-dashed border-eh-border" : "";
  const className = `${ROW_BASE} ${ROW_CLASSES[visual]} ${dashed}`;
  const KindIcon = stepKindIcon(row.kind);
  const content = (
    <>
      <RowMarker visual={visual} />
      <span data-testid={IDS.homeStepKind(row.id)} data-kind={row.kind} className="flex-shrink-0">
        <KindIcon className="h-4 w-4 text-eh-muted" aria-hidden="true" />
        <span className="sr-only">{stepKindLabel(t, row.kind)}</span>
      </span>
      <span className="min-w-0 flex-1 break-words">
        {row.title}
        <StateLabel visual={visual} />
      </span>
      {visual === "locked" && row.lock ? (
        <span data-testid={IDS.homeStepLock(row.id)} className="max-w-[45%] text-right text-xs">
          {t("pathways.home.unlocksAfter", { title: row.lock.title })}
        </span>
      ) : (
        <>
          {optional && (
            <span
              data-testid={IDS.homeStepOptional(row.id)}
              className="flex-shrink-0 rounded-full border border-eh-ochre-deep px-2.5 py-0.5 text-xs text-eh-ink"
            >
              {t("pathways.home.optional")}
            </span>
          )}
          {row.duration_minutes > 0 && (
            <span data-testid={IDS.homeStepDuration(row.id)} className="flex-shrink-0 text-xs text-eh-muted tabular-nums">
              {t("pathways.home.minutes", { n: row.duration_minutes })}
            </span>
          )}
        </>
      )}
    </>
  );
  return (
    <li data-testid={IDS.homeStep(row.id)} data-visual={visual} data-optional={optional ? "true" : "false"}>
      {isOpenable(row) ? (
        <button
          type="button"
          onClick={() => onOpenStep(row.id)}
          aria-current={row.current ? "step" : undefined}
          className={`${className} hover:brightness-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-eh-ink`}
        >
          {content}
        </button>
      ) : (
        <div className={className}>{content}</div>
      )}
    </li>
  );
}

/**
 * The pathway home (spec screen 4, mobile-first), a PURE presentational view over the
 * contract's `MemberPathwayHome` (§5.4). It is also what the practitioner's "Preview as
 * learner" renders (contract §9.1), from `previewModel.toPreviewHome(adminPathway)`.
 *
 * In the wireframe's order:
 *
 *   title
 *   Objectives
 *   "7 of 12 steps" · "58%" and the bar (`required_completed` / `required_total`)
 *   each level: "Level 1 · Basics" and its step rows. Successive and alternative hold one
 *     level whose title is not shown to learners (contract §2), so they get no header.
 *   "Continue · <current step>", sticky at the bottom of the screen: always in reach, and
 *     it opens the first incomplete unlocked step (the server's `current_step`). Absent
 *     when there is none.
 *   "You completed the pathway", with "Your certificate is also in your inbox." and
 *     "Download certificate" only once a certificate has been issued
 *     (`certificate.available`).
 *   "Weekly prompts": "Week 3 · "…"", newest first (the server's order), when there are any.
 *
 * `preview`: nothing here reaches the network except the certificate link, so preview
 * only drops that link (an admin has no certificate to download).
 *
 * @param {object} props
 * @param {object} props.home              MemberPathwayHome
 * @param {(stepId: string) => void} props.onOpenStep
 * @param {boolean} [props.preview=false]
 */
export default function PathwayHomeView({ home, onOpenStep, preview = false }) {
  const t = useT("public");
  const flat = isFlatPath(home.path_type);
  const objectives = (home.objectives || []).filter((o) => typeof o === "string" && o.trim());
  const complete = isPathwayComplete(home);
  const certificate = !preview && home.certificate?.available === true;
  const current = home.current_step;
  const prompts = Array.isArray(home.prompts) ? home.prompts : [];
  const percent = Math.max(0, Math.min(100, Number(home.percent) || 0));

  return (
    <div data-testid={IDS.home} className="mx-auto w-full max-w-xl space-y-5">
      <div className="rounded-3xl border border-eh-border-soft bg-eh-cream p-5 sm:p-6">
        <h1 className="font-display text-3xl text-eh-ink leading-tight break-words">{home.title}</h1>
        {home.status === "archived" && (
          <p className="mt-2 text-sm text-eh-muted">{t("pathways.home.archived")}</p>
        )}

        {objectives.length > 0 && (
          <section data-testid={IDS.homeObjectives} className="mt-5 rounded-2xl bg-eh-cream-deep px-4 py-3">
            <h2 className="text-sm font-semibold text-eh-ink">{t("pathways.home.objectives")}</h2>
            <ul className="mt-1 space-y-0.5 text-sm text-eh-ink">
              {objectives.map((o, i) => (
                <li key={`${i}-${o}`} className="flex gap-2 break-words">
                  <span aria-hidden="true">•</span>
                  <span className="min-w-0">{o}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        <div className="mt-5">
          <div className="flex items-baseline justify-between text-sm text-eh-ink">
            <span data-testid={IDS.homeProgress}>
              {t("pathways.home.progress", { done: home.required_completed, total: home.required_total })}
            </span>
            <span data-testid={IDS.homePercent} className="font-semibold tabular-nums">
              {t("pathways.percent", { percent })}
            </span>
          </div>
          <div
            role="progressbar"
            aria-label={t("pathways.home.progressLabel")}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={percent}
            data-testid={IDS.homeProgressBar}
            className="mt-2 h-2 w-full overflow-hidden rounded-full bg-eh-cream-deep"
          >
            <div className="h-full rounded-full bg-eh-sage-deep" style={{ width: `${percent}%` }} />
          </div>
        </div>

        <div className="mt-6 space-y-5">
          {home.levels.map((level) => (
            <section key={level.id} data-testid={IDS.homeLevel(level.id)} aria-labelledby={flat ? undefined : `pathway-level-${level.id}`}>
              {!flat && (
                <h2 id={`pathway-level-${level.id}`} data-testid={IDS.homeLevelTitle(level.id)} className="mb-2 text-sm font-semibold text-eh-ink">
                  {t("pathways.levelHeading", { n: level.index, title: level.title })}
                </h2>
              )}
              <ol className="space-y-2">
                {level.steps.map((row) => <StepRow key={row.id} row={row} onOpenStep={onOpenStep} />)}
              </ol>
            </section>
          ))}
        </div>

        {current && (
          <div className="sticky bottom-4 z-10 mt-6">
            <button
              type="button"
              onClick={() => onOpenStep(current.id)}
              data-testid={IDS.homeContinue}
              className="flex h-12 w-full items-center justify-center rounded-full bg-eh-ink px-5 text-sm font-semibold text-eh-cream shadow-lg hover:bg-eh-ink/90"
            >
              <span className="truncate">{t("pathways.home.continue", { title: current.title })}</span>
            </button>
          </div>
        )}
      </div>

      {complete && (
        <section data-testid={IDS.homeCompleted} className="rounded-3xl bg-eh-sage-soft p-5 sm:p-6">
          <h2 className="font-display text-2xl text-eh-ink">{t("pathways.home.completed")}</h2>
          {certificate && (
            <>
              <p className="mt-2 text-sm text-eh-ink">{t("pathways.home.certificateInbox")}</p>
              <a
                href={certificateUrl(home.id)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={IDS.homeCertificate}
                className="mt-4 inline-flex h-11 items-center justify-center rounded-full bg-eh-ink px-6 text-sm font-semibold text-eh-cream hover:bg-eh-ink/90"
              >
                {t("pathways.home.downloadCertificate")}
              </a>
            </>
          )}
        </section>
      )}

      {prompts.length > 0 && (
        <section data-testid={IDS.homePrompts} className="rounded-3xl border border-eh-border-soft bg-eh-cream p-5 sm:p-6">
          <h2 className="text-sm font-semibold text-eh-ink">{t("pathways.home.prompts")}</h2>
          <ul className="mt-3 space-y-2">
            {prompts.map((p) => (
              <li key={p.id} data-testid={IDS.homePrompt(p.id)} className="rounded-2xl bg-eh-cream-deep px-4 py-3 text-sm text-eh-ink whitespace-pre-line break-words">
                {t("pathways.home.prompt", { week: p.week, text: p.text })}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
